import { createContext, useContext } from 'react'

import {
    BasketProduct,
    initialOrderState,
    OrderAction,
    OrderContextType,
    OrderState
} from './orderTypes' // Reducer

const getBasketKey = (product: BasketProduct) =>
    product.sku || product.slug || String(product.id)

// Reducer
export function orderReducer(
    state: OrderState,
    action: OrderAction
): OrderState {
    switch (action.type) {
        case 'SET_CURRENT_STEP':
            return {
                ...state,
                currentStep: action.payload,
                timestamp: Date.now()
            }
        case 'SET_SESSION_ID':
            return {
                ...state,
                sessionId: action.payload,
                timestamp: Date.now()
            }
        case 'SET_VOUCHER_TYPE':
            return {
                ...state,
                voucherType: action.payload,
                timestamp: Date.now()
            }
        case 'SET_PAYMENT_METHOD':
            return {
                ...state,
                paymentMethod: action.payload,
                timestamp: Date.now()
            }
        case 'SET_AMOUNT':
            return {
                ...state,
                amount: action.payload,
                timestamp: Date.now()
            }
        case 'SET_TRANSACTION_ID':
            return {
                ...state,
                transactionId: action.payload,
                timestamp: Date.now()
            }
        case 'SET_ERROR':
            return {
                ...state,
                error: action.payload,
                timestamp: Date.now()
            }
        case 'UPDATE_ORDER_DATA':
            return {
                ...state,
                ...action.payload,
                timestamp: Date.now()
            }
        case 'ADD_BASKET_ITEM': {
            const key = getBasketKey(action.payload)
            const existing = state.basket[key]
            return {
                ...state,
                basket: {
                    ...state.basket,
                    [key]: {
                        product: action.payload,
                        qty: existing ? existing.qty + 1 : 1
                    }
                },
                timestamp: Date.now()
            }
        }
        case 'REMOVE_BASKET_ITEM': {
            const key = getBasketKey(action.payload)
            const existing = state.basket[key]
            if (!existing) {
                return state
            }
            const nextQty = existing.qty - 1
            if (nextQty <= 0) {
                const { [key]: _, ...rest } = state.basket
                return {
                    ...state,
                    basket: rest,
                    timestamp: Date.now()
                }
            }
            return {
                ...state,
                basket: {
                    ...state.basket,
                    [key]: { ...existing, qty: nextQty }
                },
                timestamp: Date.now()
            }
        }
        case 'CLEAR_BASKET':
            return {
                ...state,
                basket: {},
                timestamp: Date.now()
            }
        case 'RESET_ORDER':
            return {
                ...initialOrderState,
                sessionId: state.sessionId // Keep session ID on reset
            }
        case 'SET_SHOULD_SHOW_ARE_YOU_THERE':
            return {
                ...state,
                shouldShowAreYouThere: action.payload,
                timestamp: Date.now()
            }
        case 'SET_SELECTED_BETTING_VENDOR':
            return {
                ...state,
                selectedBettingVendor: action.payload,
                timestamp: Date.now()
            }
        case 'SET_DIRECT_BETTING_FAILED_ATTEMPTS':
            return {
                ...state,
                directBettingFailedAttempts: action.payload,
                timestamp: Date.now()
            }
        case 'SET_CREATED_VOUCHER_DATA':
            return {
                ...state,
                createdVoucherData: action.payload,
                timestamp: Date.now()
            }
        case 'SET_DIRECT_BETTING_ACCOUNT_ID':
            return {
                ...state,
                directBettingAccountId: action.payload,
                timestamp: Date.now()
            }
        case 'SET_DIRECT_BETTING_COMPLETED':
            return {
                ...state,
                directBettingCompleted: action.payload,
                timestamp: Date.now()
            }
        case 'SET_CASH_DETECTED':
            return {
                ...state,
                cashDetected: action.payload,
                timestamp: Date.now()
            }

        default:
            return state
    }
}

// Context
export const OrderContext = createContext<OrderContextType | undefined>(
    undefined
)

// Hook to use the order context
export function useOrder(): OrderContextType {
    const context = useContext(OrderContext)
    if (context === undefined) {
        throw new Error('useOrder must be used within an OrderProvider')
    }
    return context
}
